import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect, useContext } from "react";
import BlockUi from "react-block-ui";
import "react-block-ui/style.css";
import "loaders.css/loaders.min.css";

import { Loader } from "react-loaders";

import Api from "../../utils/Api";
import { findTransactionsByCategory } from "../../utils/TransactionFinder";

import Insights from "./Insights/Insights";
import Future from "./Future/Future";
import ExpenseTypeSummary from "./expenseTypeSummary/ExpenseTypeSummary";
import TransactionsAndCategoryOverview from "./TransactionsAndCategoryOverview/TransactionsAndCategoryOverview";
import SummaryAmountTag from "./SummaryAmountTag";
import MonthNavigator from "./MonthNavigator/MonthNavigator";
import SimulationPanel from "../common/simulation/SimulationPanel";
import { summaryCalculation } from "../Util";

import { AppContext } from "../../AppContext";
import { TransactionType } from "../../utils/constants";

export default forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        setTransactionsItems: (items) => {
            setTransactionsItems(items);
        },
        setCategoryItems: (categoryData) => {
            setCategoryItems(categoryData);
        }
    }));

    const { ignoredCategories, ignoredTags, selectedYear, selectedMonthId, simulatedTransactions, getTransactionSimulation } =
        useContext(AppContext);
    const [loading, setLoading] = useState(false);

    const filterInstance = useRef(null);
    const balanceAmountTagInstance = useRef(null);
    const expenseAmountTagInstance = useRef(null);
    const incomeAmountTagInstance = useRef(null);
    const expenseTypeSummaryRef = useRef(null);
    const variableExpenseInstance = useRef(null);
    const currentExpenseInstance = useRef(null);
    const incomeInstance = useRef(null);
    const insightsInstance = useRef(null);
    const futureInstance = useRef(null);

    /*
    whenver a user select a month, or change the filter, we need to reload the data
    that's why we listen to the following variables
    those variables are defined in AppContext (React context) which is updated in various places
    react context defing those variables as state, so whenever they change, this component will be re-rendered
    */
    useEffect(async () => {
        const { response: lookups } = await Api.lookups();
        filterInstance.current.setLookups(lookups);

        await loadData();
    }, [ignoredTags, ignoredCategories, selectedYear, selectedMonthId]);

    useEffect(() => {
        applySimulation();
    }, [simulatedTransactions]);

    const applySimulation = () => {
        const relevantSimulatedTransactions = getTransactionSimulation(
            selectedYear,
            selectedMonthId,
        );

        if (!relevantSimulatedTransactions || !Object.keys(relevantSimulatedTransactions).length) {
            balanceAmountTagInstance.current.setAmount(balanceAmountTagInstance.current.getOriginalAmount());
            incomeAmountTagInstance.current.setAmount(incomeAmountTagInstance.current.getOriginalAmount());
            expenseAmountTagInstance.current.setAmount(expenseAmountTagInstance.current.getOriginalAmount());
            return;
        }

        console.log(`********* simulatedTransactions changed: ${relevantSimulatedTransactions}`);

        const originalBalance = balanceAmountTagInstance.current.getOriginalAmount();
        const originalIncome = incomeAmountTagInstance.current.getOriginalAmount();
        const originalExpense = expenseAmountTagInstance.current.getOriginalAmount();

        const { newBalance, newIncome, newExpense } = summaryCalculation(
            originalBalance,
            originalIncome,
            originalExpense,
            Object.values(relevantSimulatedTransactions),
        );

        balanceAmountTagInstance.current.setAmount(newBalance);
        incomeAmountTagInstance.current.setAmount(newIncome);
        expenseAmountTagInstance.current.setAmount(newExpense);
    }

    const loadData = async () => {
        setLoading(true);
        const {
            income: originalIncome,
            expense: originalExpense,
            balance: originalBalance
        } = await getBalanceSummary();

        const relevantSimulatedTransactions = getTransactionSimulation(
            selectedYear,
            selectedMonthId,
        );

        const {
            newBalance: balance,
            newIncome: income,
            newExpense: expense
        } = summaryCalculation(
            originalBalance,
            originalIncome,
            originalExpense,
            Object.values(relevantSimulatedTransactions),
        );

        if (balanceAmountTagInstance) {
            balanceAmountTagInstance.current.setOriginalAmount(originalBalance);
            balanceAmountTagInstance.current.setAmount(balance);
        }
        if (incomeAmountTagInstance) {
            incomeAmountTagInstance.current.setOriginalAmount(originalIncome);
            incomeAmountTagInstance.current.setAmount(income);
        }
        if (expenseAmountTagInstance) {
            expenseAmountTagInstance.current.setOriginalAmount(originalExpense);
            expenseAmountTagInstance.current.setAmount(expense);
        }

        const {
            currentExpenseCategories,
            variableExpenseCategories,
            incomeCategories,
            variableExpensesSum,
            currentExpensesSum,
            currentExpensesDiffInPercentage,
            variableExpensesDiffInPercentage,
            insights,
            futureTransactions,
            simulationRecommendation
        } = await getCategorySummary();
        if (currentExpenseInstance) {
            currentExpenseInstance.current.setCategoryItems(currentExpenseCategories.reverse());
        }

        if (variableExpenseInstance) {
            variableExpenseInstance.current.setCategoryItems(variableExpenseCategories.reverse());
        }

        if (incomeInstance) {
            incomeInstance.current.setCategoryItems(incomeCategories.reverse());
        }

        if (expenseTypeSummaryRef) {
            expenseTypeSummaryRef.current.setItems({
                variableExpensesSum,
                currentExpensesSum,
                currentExpensesDiffInPercentage,
                variableExpensesDiffInPercentage
            });
        }

        if (filterInstance) {
            filterInstance.current.setSimulationRecommendation(
                simulationRecommendation,
                selectedMonthId,
                selectedYear,
            );
        }

        let { expenseTransactions, incomeTransactions } = await getTransactions();

        if (insightsInstance) {
            insights.forEach((insight) => {
                insight.transactions = findTransactionsByCategory(
                    expenseTransactions,
                    incomeTransactions,
                    insight.category.categoryId
                );
            });
            insightsInstance.current.setInsights(insights);
        }

        if (futureInstance) {
            futureInstance.current.setItems(futureTransactions.reverse());
        }

        if (incomeInstance && incomeTransactions) {
            incomeInstance.current.setTransactionsItems(incomeTransactions);
        }

        expenseTransactions = expenseTransactions.reverse();
        const currentExpenseTransactions = expenseTransactions.filter((item) => item.isCurrentCategoryTransaction);
        const variableExpenseTransactions = expenseTransactions.filter((item) => !item.isCurrentCategoryTransaction);

        if (currentExpenseInstance && currentExpenseTransactions) {
            currentExpenseInstance.current.setTransactionsItems(currentExpenseTransactions);
        }

        if (variableExpenseInstance && variableExpenseTransactions) {
            variableExpenseInstance.current.setTransactionsItems(variableExpenseTransactions);
        }
        setLoading(false);
    };

    const getBalanceSummary = async () => {
        const params = {
            year: selectedYear,
            month: selectedMonthId,
            ignoreCategories: [...ignoredCategories],
            ignoreTags: [...ignoredTags]
        };

        const { response: items } = await Api.expenseVsIncomeByTime(params);
        return items[0] ? items[0] : { income: 0, expense: 0, balance: 0 };
    };

    const getCategorySummary = async () => {
        const params = {
            year: selectedYear,
            month: selectedMonthId,
            ignoreCategories: [...ignoredCategories],
            ignoreTags: [...ignoredTags]
        };

        const { response: items } = await Api.getMonthCategories(params);

        return items;
    };

    const getTransactions = async ({
        expenseOnly = false,
        incomeOnly = false,
        includeCategoryIds = undefined
    } = {}) => {
        const params = {
            year: selectedYear,
            month: selectedMonthId,
            expenseOnly,
            incomeOnly,
            sortByAmount: true,
            ignoreCategories: [...ignoredCategories],
            ignoreTags: [...ignoredTags],
            approvedOnly: true
        };

        if (includeCategoryIds) {
            params.includeCategoryIds = includeCategoryIds;
        }

        const { response } = await Api.getMonthTransactions(params);
        return response;
    };

    return (
        <BlockUi
            tag="div"
            blocking={loading}
            loader={<Loader active type={"ball-pulse"} color="#02a17c" />}
            message={"...טוען נתונים"}
        >
            <div style={{ background: "#f8f8f8" }}>
                <div
                    style={{
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center"
                    }}
                >
                    <div
                        style={{
                            position: "fixed",
                            width: "70%",
                            top: 64,
                            zIndex: 900,
                            background: "rgb(248, 248, 248)"
                        }}
                    >
                        <MonthNavigator />
                    </div>
                </div>

                <div
                    style={{
                        justifyContent: "center",
                        display: "flex",
                        width: "100%",
                        overflow: "visible",
                        marginTop: "70px"
                    }}
                >
                    <SummaryAmountTag text={"הכנסות"} background={"#8884d8"} ref={incomeAmountTagInstance} />
                    <SummaryAmountTag text={"הוצאות"} background={"#5cd6d6"} ref={expenseAmountTagInstance} />
                    <SummaryAmountTag text={"מאזן"} background={"#4d79ff"} ref={balanceAmountTagInstance} />

                    <SimulationPanel ref={filterInstance} />
                </div>
                <div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "25px"
                        }}
                    >
                        <ExpenseTypeSummary ref={expenseTypeSummaryRef} />
                    </div>
                    <h4
                        style={{
                            display: "flex",
                            justifyContent: "right",
                            marginRight: "15%"
                        }}
                    >
                        הוצאות שוטפות
                    </h4>
                    <TransactionsAndCategoryOverview
                        mode={TransactionType.EXPENSE}
                        transactionType={TransactionType.EXPENSE}
                        ref={currentExpenseInstance}
                    />
                    <h4
                        style={{
                            display: "flex",
                            justifyContent: "right",
                            marginRight: "15%"
                        }}
                    >
                        הוצאות משתנות
                    </h4>
                    <TransactionsAndCategoryOverview
                        mode={TransactionType.EXPENSE}
                        transactionType={TransactionType.EXPENSE}
                        ref={variableExpenseInstance}
                    />

                    <h4
                        style={{
                            display: "flex",
                            justifyContent: "right",
                            marginRight: "15%",
                            marginTop: "20px"
                        }}
                    >
                        הכנסות
                    </h4>
                    <TransactionsAndCategoryOverview mode={TransactionType.INCOME} transactionType={TransactionType.INCOME} ref={incomeInstance} />

                    <h4
                        style={{
                            display: "flex",
                            justifyContent: "right",
                            marginRight: "15%",
                            marginTop: "20px"
                        }}
                    >
                        תובנות
                    </h4>
                    <Insights ref={insightsInstance} />

                    <h4
                        style={{
                            display: "flex",
                            justifyContent: "right",
                            marginRight: "15%",
                            marginTop: "20px"
                        }}
                    >
                        עסקאות עתידיות על פי ניתוח של חודשים קודמים
                    </h4>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "right",
                            width: "85%"
                        }}
                    >
                        <Future ref={futureInstance} />
                    </div>
                </div>
            </div>
        </BlockUi>
    );
});
